import {
  Autocomplete,
  AutocompleteProps,
  FormControl,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  useTheme,
} from "@mui/material";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import React, { ReactNode, useMemo } from "react";
import { Controller, FieldValues, RegisterOptions, UseControllerProps } from "react-hook-form";

import { DisplayOnlyField } from "./DisplayOnlyField";
import { ErrorDisplay } from "./ErrorDisplay";
import { TextWithTooltip } from "./TextWithTooltip";

export type AutocompleteItem = {
  value: any;
  label: string;
};

export const FormAutocomplete = <T extends FieldValues>({
  name,
  label,
  tooltip,
  disabled,
  control,
  autocompleteProps,
  autocompleteItems,
  helperText,
  rules,
  variant = "outlined",
  sx,
  InputProps = {},
  hideErrors = false,
  required = false,
}: {
  label: string;
  tooltip?: ReactNode;
  disabled?: boolean;
  autocompleteProps?: Partial<AutocompleteProps<AutocompleteItem, boolean, boolean, boolean>>;
  autocompleteItems: AutocompleteItem[];
  helperText?: string | React.JSX.Element;
  rules?: RegisterOptions;
  variant?: "standard" | "outlined" | "filled";
  sx?: SxProps<Theme>;
  InputProps?: TextFieldProps["InputProps"];
  hideErrors?: boolean;
  /** Renders the label's asterisk. `rules` still decides what's enforced. */
  required?: boolean;
} & UseControllerProps<T>) => {
  const theme = useTheme();
  const inputLabelId = useMemo(() => `${name}-label`, [name]);
  const labelWithTooltip = useMemo(
    () => (tooltip ? <TextWithTooltip text={label} tooltip={tooltip} /> : label),
    [label, tooltip]
  );
  const formContext = useValidatingFormContext();
  const isDisplayOnly = formContext?.metadata?.isDisplayOnly;

  return (
    <FormControl fullWidth sx={sx}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            {isDisplayOnly ? (
              <DisplayOnlyField
                label={labelWithTooltip}
                text={
                  autocompleteItems
                    ? (autocompleteItems.find((item) => item.value === field.value)?.label ?? "")
                    : (field.value ?? "")
                }
              />
            ) : (
              <Autocomplete
                getOptionLabel={(option: AutocompleteItem | string) =>
                  typeof option === "string" ? option : option.label
                }
                isOptionEqualToValue={(option: AutocompleteItem | string, selected) => {
                  if (typeof option === "string" && typeof selected === "string") {
                    return option === selected;
                  }
                  if (typeof option === "object" && typeof selected === "object") {
                    return option?.value === selected?.value;
                  }
                  if (typeof option === "object" && typeof selected === "string") {
                    return option?.value === selected;
                  }
                  if (typeof option === "string" && typeof selected === "object") {
                    return option === selected?.value;
                  }
                  return false;
                }}
                {...autocompleteProps}
                options={autocompleteItems}
                disabled={disabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={labelWithTooltip}
                    variant={variant}
                    required={required}
                    error={!!error}
                    InputProps={{
                      ...params.InputProps,
                      ...InputProps,
                    }}
                    InputLabelProps={{
                      id: inputLabelId,
                      style: {
                        color: disabled
                          ? theme.palette.action.disabled
                          : error
                            ? theme.palette.error.main
                            : theme.palette.text.secondary,
                      },
                    }}
                  />
                )}
                {...field}
                value={
                  autocompleteProps?.freeSolo
                    ? (field.value ?? "")
                    : (autocompleteItems.find((item) => item.value === field.value) ?? null)
                }
                onChange={(_, data) => {
                  if (autocompleteProps?.freeSolo && typeof data === "string") {
                    // Handle free text input
                    field.onChange(data);
                  } else if (
                    data &&
                    typeof data === "object" &&
                    !Array.isArray(data) &&
                    "value" in data
                  ) {
                    // Handle AutocompleteItem selection
                    field.onChange(data.value ?? null);
                  } else if (Array.isArray(data)) {
                    // Handle multiple selection (if needed in future)
                    field.onChange(
                      data.map((item) => (typeof item === "string" ? item : item.value))
                    );
                  } else {
                    // Handle null/undefined selection
                    field.onChange(null);
                  }
                }}
                onInputChange={
                  autocompleteProps?.freeSolo
                    ? (_, value) => {
                        // For freeSolo, we need to capture input changes as well
                        field.onChange(value);
                      }
                    : undefined
                }
              />
            )}
            {isDisplayOnly || hideErrors ? null : (
              <ErrorDisplay error={error} helperText={helperText} />
            )}
          </>
        )}
      />
    </FormControl>
  );
};
